import React from 'react';
import { IntlProvider } from 'react-intl';
import { Helmet } from 'react-helmet';
import get from 'lodash/get';
import Header from '../Header';
import Footer from '../Footer';
import { PageContext } from '../PageContext';
import { GlobalPageContext } from '../../types';
import defaultMessages from '../../translations/en.json';
import '../../styles/app.scss';
import { v4tov3 } from '../../lib/mapV4toV3';

type LayoutWrapperProps = GlobalPageContext;

const LayoutWrapper: React.FC<LayoutWrapperProps> = ({
  children,
  websiteLocale,
  website,
  globalScripts,
  pathPrefixes,
  availableRoutes
}) => {
  function extractGTMOriginFromScript(scriptHtml: string): string | null {
    const regex = /https?:\/\/[^/]+/g;
    const matches = scriptHtml.match(regex);
    
    if (!matches) return null;
  
    // Try to find GTM domain among matches
    const gtmDomain = matches.find(url => url.includes('googletagmanager.com'));
    return gtmDomain || null;
  }
 
  const prefetchDomains = new Set();
  const preconnectDomains = new Set();
  globalScripts?.forEach(scriptGroup => {
    scriptGroup?.scripts?.forEach(script => {
      const src = script?.attributes?.src;
      if (src) {
        try {
          // Normalize protocol-relative URLs
          const url = new URL(src.startsWith('//') ? `https:${src}` : src);
          prefetchDomains.add(url.origin);
        } catch (e) {
          console.warn('Invalid URL in script src:', src);
        }
      }
      if (script?.innerHTML) {
        const gtmdomains = extractGTMOriginFromScript(script?.innerHTML)
        preconnectDomains.add(gtmdomains)
      }
    });
  });
  return (
    <PageContext.Provider value={{ pathPrefixes, websiteLocale }}>
      <Helmet
        htmlAttributes={{
          lang: websiteLocale?.languageCode,
          lang_font: (() => {
            const arLocales = [
              "ar-OM", "ar-AE", "ar-SA", "ar-IL", "ar-TN", "ar-LY"
            ];
            if (websiteLocale?.locale === 'he-IL') return 'he-IL';
            if (websiteLocale?.locale === 'lo') return 'lo';
            if( websiteLocale?.locale === 'zh-Hant-HK') return 'zh-Hant-HK';
            if( websiteLocale?.locale === 'vi-VN') return 'vi-VN';
            if (arLocales.includes(websiteLocale?.locale)) return websiteLocale?.locale;
            return 'default_language';
          })(),
          dir: `${get(
            websiteLocale,
            'attributes.hasRightToLeftLanguage',
            websiteLocale?.hasRightToLeftLanguage ?? false
          )
            ? 'rtl'
            : 'ltr'
            }`,
        }}
      >
        {website.isTemplate ? (
          <meta name="robots" content="noindex, nofollow, noimageindex" data-no-index="true" key="no-index-ssr-key" />
        ) : null}

        {globalScripts
          ?.filter((i) => i.loadInHead)
          .flatMap((item, i) => [
            ...item.scripts.map((script) => {
              const scriptProps = {
                key: `global-head-script-${i}`,
                type: "text/javascript",
                ...script.attributes,
                defer: item.loadDefer ?? false,
              };
              if (item.loadAsync) {
                scriptProps.async = true;
              }
              return (
                <script {...scriptProps}>
                  {`${script.innerHTML}`}
                </script>
              );
            }),
            ...item.noscripts.map((noscript) => (
              <noscript key={`global-head-noscript-${i}`}>{`${noscript}`}</noscript>
            )),
          ])}
        <link rel="preconnect" href="https://mktmedia.wallstreetenglish.com" />
        <link rel="dns-prefetch" href="https://mktmedia.wallstreetenglish.com" />

        {Array.from(prefetchDomains).map((domain, i) => (
          <link
            key={`prefetch-domain-${i}`}
            rel="dns-prefetch"
            href={domain}
          />
        ))}
        {Array.from(preconnectDomains).map((domain, i) => (
          <link
            key={`preconnect-domain-${i}`}
            rel="preconnect"
            href={domain}
            crossOrigin="true"
          />
        ))}
      </Helmet>
      <Header
        websiteLocale={websiteLocale}
        isInternationalSite={website.settings?.isInternationalSite ?? false}
        pathPrefixes={pathPrefixes}
        id={website.documentId}
        availableRoutes={availableRoutes}
      />
      <div id="main-root">{children}</div>
      <Footer websiteLocale={websiteLocale} />
      {globalScripts
        ?.filter((i) => !i.loadInHead)
        .flatMap((item, i) => [
          ...item.scripts.map((script, j) => {
            const scriptProps = {
              key: `global-body-script-${i}-${j}`,
              type: 'text/javascript',
              ...script.attributes,
              defer: item.loadDefer ?? false,
              dangerouslySetInnerHTML: { __html: script.innerHTML ?? '' },
            };
            if (item.loadAsync) {
              scriptProps.async = true;
            }
            return (
              <script {...scriptProps} />
            );
          }),
          ...item.noscripts.map((noscript, k) => (
            <noscript key={`global-body-noscript-${i}-${k}`}>{noscript}</noscript>
          )),
        ])}
    </PageContext.Provider>
  );
};

export default LayoutWrapper;

export const intlWrapperHOC = (Component: React.ComponentType<any>) => {
  const wrappedComponent = (props: { pageContext: GlobalPageContext }) => {
    const messages = {
      ...defaultMessages,
      ...(props.pageContext.websiteLocale.translations ?? {})
    };
    return (
      <IntlProvider messages={messages} locale={get(props, 'pageContext.websiteLocale.languageCode', 'en')}>
        <Component {...props} />
      </IntlProvider>
    );
  };
  return wrappedComponent;
};

export const v4tov3HOC = (Component: React.ComponentType<any>) => {
  const V4ToV3Mapper = (props: { data?: any }) => {
    const { data, ...rest } = props;
    const newData = v4tov3(data);
    return <Component data={newData} {...rest} />;
  };
  return V4ToV3Mapper;
};